import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { login, loginWithGoogle } from "../../firebase/auth";
import { useAuth } from "../../context/AuthContext";
import { getAuth, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import Input from "../../components/Input/Input";
import Button from "../../components/Button/Button";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Extract redirect param if present
    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect") || "/";

    useEffect(() => {
        if (user) navigate(redirect);
    }, [user, navigate, redirect]);

    // Email/password login
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            navigate(redirect);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Google SSO login
    const handleGoogleLogin = async () => {
        setError("");
        setLoading(true);
        const auth = getAuth();
        const provider = new GoogleAuthProvider();
        try {
            const result = await signInWithPopup(auth, provider);
            await loginWithGoogle(result.user);
            navigate(redirect);
        } catch (err) {
            console.error("Google sign-in error:", err);
            setError("Google sign-in failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-full flex items-center justify-center bg-background mt-12">
            <div className="max-w-md w-full bg-white p-8 shadow rounded-lg">
                <h1 className="text-xl font-bold mb-3 text-center">Login</h1>
                <p className="text-xs text-center mb-3">Disclaimer: This app is in beta mode.</p>
                {error && <p className="text-red-500 mb-4 text-center">{error}</p>}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <Input
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Email"
                        required
                    />
                    <Input
                        type="password"
                        name="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Password"
                        required
                    />
                    <Button type="submit" variant="primary" disabled={loading} className="w-full">
                        {loading ? "Logging in..." : "Login"}
                    </Button>
                </form>

                <div className="flex items-center my-4">
                    <hr className="flex-grow border-surface" />
                    <span className="mx-2">or</span>
                    <hr className="flex-grow border-surface" />
                </div>

                <Button
                    onClick={handleGoogleLogin}
                    variant="ghost"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 border border-surface text-text rounded-md py-2 hover:bg-gray-50 transition"
                >
                    <img
                        src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                        alt="Google"
                        className="w-5 h-5"
                    />
                    {loading ? "Signing in..." : "Sign in with Google"}
                </Button>

                <p className="mt-4 text-center">
                    Don’t have an account?{" "}
                    <Link
                        to={`/signup${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`}
                        className="text-primary hover:underline"
                    >
                        Sign Up
                    </Link>
                </p>
            </div>
        </div>
    );
}
